import React, { useEffect, useState, useRef } from "react";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import {
  Text,
  Flex,
  TextField,
  View,
  Picker,
  Item,
  TextArea,
  Button,
  ComboBox,
  Section,
  DialogContainer,
  Dialog,
  Heading,
  Divider,
  Content,
  DatePicker,
  ProgressCircle,
} from "@adobe/react-spectrum";
import { useAsyncList } from "react-stately";
import { ToastQueue } from '@adobe/react-spectrum';
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import { CreateProjectItemRequest } from "../../../../services/soap/item/requests/CreateProjectItemRequest";
import { UpdateProjectItemRequest } from "../../../../services/soap/item/requests/UpdateProjectItemRequest";
import { generateProjectProductItem } from "../../../../services/soap/item/requests/CreateProjectItemOptions";
import { ListCurrencyRateRequest } from "../../../../services/soap/finance/requests/ListCurrencyRateRequest";
import { GetFinanceSettingsRequest } from "../../../../services/soap/finance/requests/GetFinanceSettingsRequest";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { ListItemRequest } from "../../../../services/soap/item/requests/ListItemRequest";
import { ListFPARequest2 } from "../../../../services/soap/project/requests/ListFPARequest2";
import { parseDate } from "@internationalized/date";
import NumericTextField from "../../../ui/NumericTextField";
import moment from "moment";
import { useTranslation } from "react-i18next";
import styles from "./item_create_update.module.css";

interface ItemCreateUpdateProps {
  close: () => void;
  selectedItem?: FPAData;
  itemData?: any;
  setIsCreateOpen: any;
  isCreateOpen: boolean;
  searchProducts?: (query: string) => Promise<any[]>;
  currencies?: any[];
  activeAttributesData?: any[];
  onSave?: () => void;
}

const ItemCreateUpdate: React.FC<ItemCreateUpdateProps> = ({
  close,
  selectedItem,
  itemData,
  setIsCreateOpen,
  isCreateOpen,
  searchProducts,
  currencies = [],
  activeAttributesData = [],
  onSave,
}) => {
  const { isMobile, isIPadPro } = useViewInfo();
  const { t } = useTranslation();
  const { itemService, store, projectService, financeService } =
    useDependency();
  const [visibility, setVisibility] = useState<boolean>(isCreateOpen);
  const [partNumberData, setPartNumberData] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [orgCurrency, setOrgCurrency] = useState<string>("");
  const txtProductRef = useRef<any>(null);
  // On edit: holds the refId that the load() function should auto-select once results arrive
  const pendingEditRefIdRef = useRef<string | null>(null);

  // Fetch organisation's local (base) currency once on mount
  useEffect(() => {
    let active = true;
    financeService
      .getFinanceSettings(
        new GetFinanceSettingsRequest(store.Server, store.SessionId),
      )
      .then((response: any) => {
        if (!active) return;
        const settings = response?.SETTINGS;
        // settings.currency is a currency ID, not an ISO code — look it up in the currencies list
        const currencyId = String(
          Array.isArray(settings)
            ? settings?.[0]?.currency
            : settings?.currency || "",
        ).trim();
        if (!currencyId) return;
        const found = currencies.find((c: any) => String(c.id) === currencyId);
        const code = found?.code || "";
        if (code) setOrgCurrency(code);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRecordIds = (record: any, item: FPAData | undefined) => {
    switch (item?.type) {
      case FPADataTypes.ACTIVITY:
        record.folderId = item?.parent?.parent_id ?? "";
        record.projectId = String(item?.parent_id ?? "");
        record.activityId = String(item?.id ?? "");
        break;
      case FPADataTypes.PROJECT:
        record.folderId = "";
        record.projectId = String(item?.id ?? "");
        record.activityId = "";
        break;
      case FPADataTypes.FOLDER:
        record.folderId = String(item?.id ?? "");
        record.projectId = "";
        record.activityId = "";
        break;
      default:
        record.folderId = "";
        record.projectId = "";
        record.activityId = "";
    }
  };

  // Supplier (provider) state
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null,
  );
  const [providerInputValue, setProviderInputValue] = useState<string>("");

  // Supplier async list
  const providerList = useAsyncList({
    async load({ filterText }) {
      if (!filterText || filterText.length < 2) {
        return { items: [] };
      }
      try {
        const result = await projectService.listFPA2(
          new ListFPARequest2(store.Server, store.SessionId, {
            limit: 50,
            offset: 0,
            flatView: true,
            fulltext: filterText + "*",
            folderFilter: {
              COL: [
                {
                  name: "fpaTypeCategories",
                  type: "any",
                  value: "com.atollon.project.fpatypecategory.organization",
                },
              ],
            },
            ORDER: {
              COL: [
                { name: "name", desc: false },
                { name: "created", desc: true },
              ],
            },
          }),
        );
        if (!result || result.count === 0 || !result.ITEMS) {
          return { items: [] };
        }
        const items = result.ITEMS.map((item: any) => {
          const inner = item.ITEM.FOLDER;
          return {
            id: inner.id,
            name: `${inner.name}`,
          };
        });
        return { items };
      } catch (error) {
        console.error("Error fetching supplier list:", error);
        return { items: [] };
      }
    },
  });

  // Form state - property names match itemData for direct save without remapping
  const [formData, setFormData] = useState({
    _refId: "",
    partNumber: "",
    partID: "",
    name: "",
    supplier: "",
    customFields: "",
    billingDate: "",
    purchaseCurrency: "CZK",
    purchasePrice: "",
    currencyRate: "1",
    catalogPrice: "",
    discount: "",
    currency: "USD",
    amount: "",
    quantity: "",
    vatValue: "0",
    totalAmount: "",
    attrib0: "",
    attrib0_id: "",
    attrib0Name: "",
    attrib1: "",
    attrib1_id: "",
    attrib1Name: "",
    attrib2: "",
    attrib2_id: "",
    attrib2Name: "",
    attrib3: "",
    attrib3_id: "",
    attrib3Name: "",
    attrib4: "",
    attrib4_id: "",
    attrib4Name: "",
    attrib5: "",
    attrib5_id: "",
    attrib5Name: "",
    attrib6: "",
    attrib6_id: "",
    attrib6Name: "",
    attrib7: "",
    attrib7_id: "",
    attrib7Name: "",
    attrib8: "",
    attrib8_id: "",
    attrib8Name: "",
    attrib9: "",
    attrib9_id: "",
    attrib9Name: "",
    note: "",
    dimension1: "",
    dimension2: "",
    dimension3: "",
  });

  // Initialize form with row data
  useEffect(() => {
    setVisibility(isCreateOpen);
    if (itemData) {
      setFormData({
        // On edit, don't pre-set _refId to a guessed key — the async list may load
        // items with slightly different names causing intermittent key mismatch.
        // _refId is only set when the user actively selects from the dropdown.
        _refId: itemData._refId || "",
        partNumber: itemData.refId || "",
        partID: itemData.partID || itemData.item || "",
        name:
          itemData.name &&
          itemData.refId &&
          !itemData.name.includes(`(${itemData.refId})`)
            ? `${itemData.name}`
            : itemData.name || "",
        supplier: itemData.supplier || "",
        customFields: itemData.customFields || "",
        billingDate: itemData.billingDate || "",
        purchaseCurrency: itemData.purchaseCurrency || "CZK",
        purchasePrice: itemData.purchasePrice || "",
        currencyRate: itemData.currencyRate || "1",
        catalogPrice: itemData.catalogPrice || "",
        discount: itemData.discount || "",
        currency: itemData.currency || "USD",
        amount: itemData.amount || itemData.unitPrice || "",
        quantity: itemData.quantity || "",
        vatValue: String(itemData.vatValue ?? "0"),
        totalAmount: itemData.totalPrice || "",
        attrib0: itemData.attrib0 || "",
        attrib0_id: itemData.attrib0_id || "",
        attrib0Name: activeAttributesData[0]?.name ?? "",
        attrib1: itemData.attrib1 || "",
        attrib1_id: itemData.attrib1_id || "",
        attrib1Name: activeAttributesData[1]?.name ?? "",
        attrib2: itemData.attrib2 || "",
        attrib2_id: itemData.attrib2_id || "",
        attrib2Name: activeAttributesData[2]?.name ?? "",
        attrib3: itemData.attrib3 || "",
        attrib3_id: itemData.attrib3_id || "",
        attrib3Name: activeAttributesData[3]?.name ?? "",
        attrib4: itemData.attrib4 || "",
        attrib4_id: itemData.attrib4_id || "",
        attrib4Name: activeAttributesData[4]?.name ?? "",
        attrib5: itemData.attrib5 || "",
        attrib5_id: itemData.attrib5_id || "",
        attrib5Name: activeAttributesData[5]?.name ?? "",
        attrib6: itemData.attrib6 || "",
        attrib6_id: itemData.attrib6_id || "",
        attrib6Name: activeAttributesData[6]?.name ?? "",
        attrib7: itemData.attrib7 || "",
        attrib7_id: itemData.attrib7_id || "",
        attrib7Name: activeAttributesData[7]?.name ?? "",
        attrib8: itemData.attrib8 || "",
        attrib8_id: itemData.attrib8_id || "",
        attrib8Name: activeAttributesData[8]?.name ?? "",
        attrib9: itemData.attrib9 || "",
        attrib9_id: itemData.attrib9_id || "",
        attrib9Name: activeAttributesData[9]?.name ?? "",
        note: itemData.note || "",
        dimension1: itemData.dimension1 || "",
        dimension2: itemData.dimension2 || "",
        dimension3: itemData.dimension3 || "",
      });
      // Set filter text for ComboBox using the actual refId (partNumber in grid data is hardcoded)
      if (itemData.refId) {
        pendingEditRefIdRef.current = itemData.refId;
        list.setFilterText(itemData._refId);
      }
      // Initialise supplier ComboBox
      setSelectedProviderId(itemData.supplier || null);
      setProviderInputValue(itemData.supplierName || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateOpen, itemData]);

  // Auto-calculate Total Amount
  useEffect(() => {
    const unitPrice = parseFloat(formData.amount) || 0;
    const qty = parseFloat(formData.quantity) || 0;
    const total = unitPrice * qty;

    setFormData((prev) => ({
      ...prev,
      totalAmount: total.toFixed(2),
    }));
  }, [formData.amount, formData.quantity]);

  // Calculate Including VAT for footer
  const calculateIncludingVAT = () => {
    const total = parseFloat(formData.totalAmount) || 0;
    const vatPercent = parseFloat(formData.vatValue) || 0;
    return (total * (1 + vatPercent / 100)).toFixed(2);
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Find object by ID helper function
  const findObjectById = (id: string) => {
    if (!id || typeof id !== "string") {
      return null;
    }
    let newId = id.endsWith("_ref")
      ? id.replace("_ref", "_name")
      : id.replace("_name", "_ref");

    for (const group of partNumberData) {
      const found = group.children.find(
        (child: any) => child._refId === id || child._refId === newId,
      );
      if (found) return found;
    }
    return null;
  };

  // Async list for ComboBox
  const list = useAsyncList<any>({
    async load({ filterText }) {
      if (!filterText || !filterText.trim()) {
        return { items: [], cursor: undefined };
      }
      const [itemResp, itemRespByRefID]: any = await Promise.all([
        itemService.listItem(
          new ListItemRequest(store.Server, store.SessionId, 10, 0, {
            searchName: filterText.trim(),
          }),
        ),
        itemService.listItem(
          new ListItemRequest(store.Server, store.SessionId, 10, 0, {
            refId: filterText.trim(),
          }),
        ),
      ]);

      const formatItemRefID = (ITEM: any) => ({
        id: ITEM.id,
        refId: ITEM.refId,
        _refId: `${ITEM.refId}_${ITEM.name}_ref`,
        refIdName: `${ITEM.refId}(${ITEM.name})`,
        productName: ITEM.name,
        unitPrice: ITEM.amount,
        amountWithVat: ITEM.amountWithVat,
        vatValue: ITEM.vatValue,
        purchasePrice: ITEM.purchasePrice,
        totalAmountWithVat: ITEM.totalAmountWithVat,
        itemType: ITEM.type,
      });

      const formatItemName = (ITEM: any) => ({
        id: ITEM.id,
        refId: ITEM.refId,
        _refId: `${ITEM.refId}_${ITEM.name}_name`,
        refIdName: `${ITEM.name}${ITEM.refId ? `(${ITEM.refId})` : ""}`,
        productName: ITEM.name,
        unitPrice: ITEM.amount,
        amountWithVat: ITEM.amountWithVat,
        vatValue: ITEM.vatValue,
        purchasePrice: ITEM.purchasePrice,
        totalAmountWithVat: ITEM.totalAmountWithVat,
        itemType: ITEM.type,
      });

      const options = [
        ...(itemRespByRefID.ITEM?.length
          ? [
              {
                name: `${itemRespByRefID.ITEM.length} ref id match`,
                children: itemRespByRefID.ITEM.map(formatItemRefID),
              },
            ]
          : []),
        ...(itemResp.ITEM?.length
          ? [
              {
                name: `${itemResp.ITEM.length} name match`,
                children: itemResp.ITEM.map(formatItemName),
              },
            ]
          : []),
      ];
      setPartNumberData(options);

      // Auto-select matching item when opening in edit mode
      if (pendingEditRefIdRef.current) {
        const targetRefId = pendingEditRefIdRef.current;
        const exactMatch = itemRespByRefID.ITEM?.find(
          (ITEM: any) => ITEM.refId === targetRefId,
        );
        if (exactMatch) {
          pendingEditRefIdRef.current = null;
          const formatted = formatItemRefID(exactMatch);
          setFormData((prev) => ({ ...prev, _refId: formatted._refId }));
        }
      }

      return { items: options, cursor: undefined };
    },
  });

  // Handle input change in ComboBox
  const onInputChange = (value: any) => {
    list.setSelectedKeys(value === "" ? new Set<any>() : list.selectedKeys);
    list.setFilterText(value === "" ? " " : value);
  };

  // Handle selection change in ComboBox
  const selectedIndexChanged = async (value: any) => {
    if (!value) return;

    let selectedItem: any = findObjectById(value);

    if (selectedItem) {
      list.setFilterText(`${selectedItem.refId} (${selectedItem.productName})`);
      list.setSelectedKeys(new Set([selectedItem._refId]));
      setFormData((prev) => ({
        ...prev,
        _refId: selectedItem._refId,
        partNumber: selectedItem.refId,
        name: `${selectedItem.productName}`,
        partID: selectedItem.id,
        amount: selectedItem.unitPrice,
        vatValue: String(selectedItem.vatValue ?? "0"),
      }));
      // Focus next field
      setTimeout(() => {
        txtProductRef.current?.focus();
      }, 100);
    } else {
      if (itemData) {
        // If user typed a value that doesn't match any item, but we have existing itemData (edit mode), reset to original value
        pendingEditRefIdRef.current = itemData._refId;
        list.setFilterText(`${itemData._refId} `);
        list.setSelectedKeys(new Set([itemData._refId]));
        return;
      }

      list.setFilterText("");
      list.setSelectedKeys(new Set());
      setFormData((prev) => ({ ...prev, _refId: "", partNumber: "" }));
    }
  };

  const buildProjectItem = (record: any) => {
    const quantity = parseFloat(formData.quantity) || 1;
    const unitPrice = parseFloat(formData.amount) || 0;
    const totalPrice = parseFloat(formData.totalAmount) || quantity * unitPrice;
    const vatPct = parseFloat(formData.vatValue) || 0;
    const totalAmountWithVat = totalPrice * (1 + vatPct / 100);

    const builtItem = generateProjectProductItem(
      record.folderId || "",
      record.projectId || "",
      record.activityId || "",
      formData.partID || "",
      formData.name,
      quantity,
      unitPrice,
      totalPrice,
      totalAmountWithVat,
      formData.currency || "",
      formData.vatValue || "0",
      formData.purchasePrice || "0",
      formData.attrib0 || "",
      formData?.attrib0_id || "",
      formData?.attrib1 || "",
      formData?.attrib1_id || "",
      formData.attrib2 || "",
      formData?.attrib2_id || "",
      formData.attrib3 || "",
      formData?.attrib3_id || "",
      formData.attrib4 || "",
      formData?.attrib4_id || "",
      formData.attrib5 || "",
      formData?.attrib5_id || "",
      formData.attrib6 || "",
      formData?.attrib6_id || "",
      formData.attrib7 || "",
      formData?.attrib7_id || "",
      formData.attrib8 || "",
      formData?.attrib8_id || "",
      formData.attrib9 || "",
      formData?.attrib9_id || "",
      formData.supplier || "",
      itemData?.refId || "",
      formData.billingDate
        ? moment(formData.billingDate).format("YYYY-MM-DD")
        : new Date().toISOString().split("T")[0],
      itemData?.billingStatus || "0",
      itemData?.parentId || undefined,
      {
        ...itemData,
        ratio:
          itemData?.ratioValue ||
          (itemData?.ratio
            ? String(itemData.ratio).replace("%", "").trim()
            : "100"),
        dimension1: formData.dimension1,
        dimension2: formData.dimension2,
        dimension3: formData.dimension3,
        note: formData.note,
        catalogPrice: formData.catalogPrice || "",
        purchaseCurrency: formData.purchaseCurrency || "",
        currencyRate: formData.currencyRate || "1",
      },
    );
    // generateProjectProductItem always overwrites purchaseCurrency with the sale
    // currency and catalogPrice with unitPrice — restore the correct form values.
    builtItem.purchaseCurrency = formData.purchaseCurrency || "";
    builtItem.catalogPrice = formData.catalogPrice || "";
    return builtItem;
  };

  const handleSave = async (andNew = false) => {
    if (!selectedItem?.id) {
      ToastQueue.negative(
        t("no_project_selected", { ns: "layout_components" }),
        { timeout: 3000 },
      );
      return;
    }
    if (!formData.name && !formData.partID) {
      ToastQueue.negative(
        t("please_select_a_product", { ns: "layout_components" }),
        { timeout: 3000 },
      );
      return;
    }

    // Validate that purchasePrice * quantity does not overflow the DB field
    // itm_calculation.total_purchase has precision 12, scale 2 (max 9,999,999,999.99)
    const MAX_DB_NUMERIC = 9_999_999_999.99;
    const purchasePriceVal = parseFloat(formData.purchasePrice) || 0;
    const quantityVal = parseFloat(formData.quantity) || 1;
    const currencyRateVal = parseFloat(formData.currencyRate) || 1;
    const purchaseCurrencyRateVal =
      parseFloat(itemData?.purchaseCurrencyRate || "1") || 1;
    const totalPurchaseEstimate =
      (purchasePriceVal * quantityVal * purchaseCurrencyRateVal) /
      currencyRateVal;
    if (totalPurchaseEstimate > MAX_DB_NUMERIC) {
      ToastQueue.negative(
        t("total_purchase_value_exceeds_max", {
          ns: "layout_components",
          defaultValue:
            "Total purchase value ({{value}}) exceeds the maximum allowed value of 9,999,999,999.99",
          value: totalPurchaseEstimate.toFixed(2),
        }),
        { timeout: 5000 },
      );
      return;
    }

    setShowLoader(true);
    try {
      const record: any = {};
      setRecordIds(record, selectedItem);
      const isEditing = itemData && itemData.id;

      if (isEditing) {
        const result = await itemService.updateProjectItem(
          new UpdateProjectItemRequest(
            store.Server,
            store.SessionId,
            buildProjectItem(record),
          ),
        );
        if (result.result === "OK") {
          ToastQueue.positive(
            t("item_updated_successfully", { ns: "layout_components" }),
            { timeout: 3000 },
          );
          onSave?.();
        } else {
          ToastQueue.negative(
            result.EXCEPTION?.reason ||
              t("failed_to_update_item", { ns: "layout_components" }),
            { timeout: 3000 },
          );
          return;
        }
      } else {
        const result = await itemService.createProjectItem(
          new CreateProjectItemRequest(
            store.Server,
            store.SessionId,
            buildProjectItem(record),
          ),
        );
        if (result.result === "OK") {
          ToastQueue.positive(
            t("item_created_successfully", { ns: "layout_components" }),
            { timeout: 3000 },
          );
          onSave?.();
        } else {
          ToastQueue.negative(
            result.EXCEPTION?.reason ||
              t("failed_to_create_item", { ns: "layout_components" }),
            { timeout: 3000 },
          );
          return;
        }
      }

      if (andNew) {
        // Reset form for new item
        setFormData({
          _refId: "",
          partNumber: "",
          partID: "",
          name: "",
          supplier: "",
          customFields: "",
          billingDate: "",
          purchaseCurrency: "CZK",
          purchasePrice: "",
          currencyRate: "1",
          catalogPrice: "",
          discount: "",
          currency: "USD",
          amount: "",
          quantity: "",
          vatValue: "0",
          totalAmount: "",
          attrib0: "",
          attrib0_id: "",
          attrib0Name: activeAttributesData[0]?.name ?? "",
          attrib1: "",
          attrib1_id: "",
          attrib1Name: activeAttributesData[1]?.name ?? "",
          attrib2: "",
          attrib2_id: "",
          attrib2Name: activeAttributesData[2]?.name ?? "",
          attrib3: "",
          attrib3_id: "",
          attrib3Name: activeAttributesData[3]?.name ?? "",
          attrib4: "",
          attrib4_id: "",
          attrib4Name: activeAttributesData[4]?.name ?? "",
          attrib5: "",
          attrib5_id: "",
          attrib5Name: activeAttributesData[5]?.name ?? "",
          attrib6: "",
          attrib6_id: "",
          attrib6Name: activeAttributesData[6]?.name ?? "",
          attrib7: "",
          attrib7_id: "",
          attrib7Name: activeAttributesData[7]?.name ?? "",
          attrib8: "",
          attrib8_id: "",
          attrib8Name: activeAttributesData[8]?.name ?? "",
          attrib9: "",
          attrib9_id: "",
          attrib9Name: activeAttributesData[9]?.name ?? "",
          note: "",
          dimension1: "",
          dimension2: "",
          dimension3: "",
        });
        setSelectedProviderId(null);
        setProviderInputValue("");
        list.setFilterText("");
        list.setSelectedKeys(new Set());
      } else {
        closeDialog();
      }
    } catch (error: any) {
      console.error("Error saving item:", error);
      ToastQueue.negative(
        error?.message || t("failed_to_save_item", { ns: "layout_components" }),
        {
          timeout: 3000,
        },
      );
    } finally {
      setShowLoader(false);
    }
  };

  const handleSaveAndNew = () => handleSave(true);

  const handlePurchaseCurrencyChange = async (key: any) => {
    const newCurrency = String(key);
    // Update currency immediately so UX is responsive
    handleFieldChange("purchaseCurrency", newCurrency);

    if (newCurrency === (orgCurrency || "CZK")) {
      handleFieldChange("currencyRate", "1");
      return;
    }

    try {
      const currencyObj = currencies.find((c: any) => c.code === newCurrency);
      if (!currencyObj) return;

      // Use the billing date if set, otherwise today
      const rateDate = formData.billingDate
        ? moment(formData.billingDate, "YYYY-MM-DD").format("YYYY-MM-DD") +
          "T00:00:00"
        : new Date().toISOString().split("T")[0] + "T00:00:00";

      const res: any = await financeService.listCurrencyRate(
        new ListCurrencyRateRequest(store.Server, store.SessionId, {
          currency: currencyObj.id,
          rateDate,
        }),
      );

      if (res?.currencyRate?.rate) {
        handleFieldChange("currencyRate", String(res.currencyRate.rate));
      }
    } catch (error) {
      console.error("Error fetching currency rate:", error);
      ToastQueue.negative(
        t("failed_to_fetch_currency_rate", { ns: "layout_components" }),
        { timeout: 3000 },
      );
    }
  };

  const closeDialog = () => {
    setVisibility(false);
    setIsCreateOpen(false);
    close();
  };

  const handleClose = () => {
    closeDialog();
  };

  function dialogContent(): React.ReactElement {
    return (
      <Flex direction="column" gap="size-200">
        {/* ── Section 1: Product ──────────────────────────────── */}
        <View UNSAFE_className={styles.section_card}>
          <Flex direction="row" gap="size-200" alignItems="end">
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("item", { ns: "layout_components", defaultValue: "Item" })}:
              </Text>
              <ComboBox
                id="cmbPartNumber"
                aria-label="none"
                width="100%"
                menuWidth={400}
                items={list.items}
                selectedKey={formData._refId || null}
                inputValue={list.filterText || formData._refId || ""}
                defaultInputValue={formData._refId || ""}
                onInputChange={onInputChange}
                onSelectionChange={selectedIndexChanged}
                loadingState={list.loadingState}
                onLoadMore={list.loadMore}
                UNSAFE_className="borderGrey"
              >
                {(item: any) => (
                  <Section
                    key={item.name}
                    items={item.children}
                    title={item.name}
                  >
                    {(item: any) => (
                      <Item key={item._refId}>{item.refIdName}</Item>
                    )}
                  </Section>
                )}
              </ComboBox>
            </Flex>
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("name", { ns: "layout_components", defaultValue: "Name" })}:
              </Text>
              <TextField
                ref={txtProductRef}
                width="100%"
                value={formData.name}
                onChange={(value) => handleFieldChange("name", value)}
              />
            </Flex>
          </Flex>
        </View>

        {/* ── Section 2: Supplier ─────────────────────────────── */}
        <View UNSAFE_className={styles.section_card}>
          <Flex direction="row" gap="size-200" alignItems="start">
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("supplier", {
                  ns: "layout_components",
                  defaultValue: "Supplier",
                })}
                :
              </Text>
              <ComboBox
                UNSAFE_className="borderGrey"
                width="100%"
                aria-label={t("supplier", {
                  ns: "layout_components",
                  defaultValue: "Supplier",
                })}
                placeholder={t("type_to_search", {
                  ns: "layout_components",
                  defaultValue: "Type to search",
                })}
                items={providerList.items}
                inputValue={providerInputValue}
                onInputChange={(value) => {
                  setProviderInputValue(value);
                  providerList.setFilterText(value);
                }}
                loadingState={providerList.loadingState}
                selectedKey={selectedProviderId}
                onSelectionChange={(key) => {
                  if (key) {
                    const keyStr = String(key);
                    const selected: any = providerList.items.find(
                      (p: any) => p.id === keyStr,
                    );
                    if (selected) {
                      setProviderInputValue(selected.name);
                      setSelectedProviderId(keyStr);
                      handleFieldChange("supplier", keyStr);
                    }
                  } else {
                    setSelectedProviderId(null);
                    setProviderInputValue("");
                    handleFieldChange("supplier", "");
                  }
                }}
                allowsCustomValue
                menuTrigger="input"
              >
                {(item: any) => <Item key={item.id}>{item.name}</Item>}
              </ComboBox>
            </Flex>
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("custom_fields", {
                  ns: "layout_components",
                  defaultValue: "Custom Fields",
                })}
                :
              </Text>
              <TextArea
                width="100%"
                value={formData.customFields}
                onChange={(value) => handleFieldChange("customFields", value)}
                placeholder={t("barcode", {
                  ns: "layout_components",
                  defaultValue: "Barcode",
                })}
                isReadOnly
              />
            </Flex>
          </Flex>
        </View>

        {/* ── Section 3: Billing Date ─────────────────────────── */}
        <View UNSAFE_className={styles.section_card}>
          <Flex direction="column" gap="size-50">
            <Text UNSAFE_className={styles.field_label}>
              {t("billing_date", {
                ns: "layout_components",
                defaultValue: "Billing Date",
              })}
              :
            </Text>
            <DatePicker
              width="100%"
              value={
                formData?.billingDate
                  ? parseDate(
                      moment(formData?.billingDate, "YYYY-MM-DD").format(
                        "YYYY-MM-DD",
                      ),
                    )
                  : undefined
              }
              onChange={(date) => {
                const newDate = moment(date?.toString()).format("YYYY-MM-DD");
                handleFieldChange("billingDate", newDate);
              }}
              UNSAFE_className={`${styles.date_picker_field} datePicker`}
            />
          </Flex>
        </View>

        {/* ── Section 4: Purchase ─────────────────────────────── */}
        <View UNSAFE_className={styles.section_card}>
          <Flex direction="row" gap="size-200" alignItems="end">
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("purchase", {
                  ns: "layout_components",
                  defaultValue: "Purchase",
                })}
                :
              </Text>
              <Flex direction="row" gap="size-100" alignItems="center">
                <Picker
                  width="105px"
                  UNSAFE_className={`${styles.picker_field} borderGrey`}
                  selectedKey={formData.purchaseCurrency}
                  onSelectionChange={handlePurchaseCurrencyChange}
                >
                  {currencies.map((currency: any) => (
                    <Item key={currency.code}>{currency.code}</Item>
                  ))}
                </Picker>
                <Flex flex={1} UNSAFE_style={{ minWidth: 0 }}>
                  <NumericTextField
                    width="100%"
                    value={formData.purchasePrice}
                    onChange={(value) =>
                      handleFieldChange("purchasePrice", value)
                    }
                  />
                </Flex>
              </Flex>
            </Flex>
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("exchange_rate", {
                  ns: "layout_components",
                  defaultValue: "Exchange rate",
                })}
                :
              </Text>
              <Flex direction="row" gap="size-100" alignItems="center">
                <TextField
                  width="100%"
                  value={formData.currencyRate}
                  onChange={(value) => handleFieldChange("currencyRate", value)}
                />
                <Text
                  UNSAFE_style={{
                    fontSize: "13px",
                    color: "#6e6e6e",
                    whiteSpace: "nowrap",
                  }}
                >
                  {orgCurrency || formData.purchaseCurrency}{" "}
                  {(
                    parseFloat(formData.purchasePrice || "0") *
                    parseFloat(formData.currencyRate || "1")
                  ).toFixed(2)}
                </Text>
              </Flex>
            </Flex>
          </Flex>
          <Flex direction="column" gap="size-50">
            <Text UNSAFE_className={styles.field_label}>
              {t("catalog_price", {
                ns: "layout_components",
                defaultValue: "Catalog price",
              })}
              :
            </Text>
            <NumericTextField
              width="100%"
              value={formData.catalogPrice}
              onChange={(value) => handleFieldChange("catalogPrice", value)}
            />
          </Flex>
        </View>

        {/* ── Section 5: Pricing ──────────────────────────────── */}
        <View UNSAFE_className={styles.section_card}>
          <Flex direction="row" gap="size-200" alignItems="end">
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("unit_price", {
                  ns: "layout_components",
                  defaultValue: "Unit price",
                })}
                :
              </Text>
              <NumericTextField
                width="100%"
                value={formData.amount}
                onChange={(value) => handleFieldChange("amount", value)}
                prefix={formData.currency || "CZK"}
              />
            </Flex>
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("quantity", {
                  ns: "layout_components",
                  defaultValue: "Quantity",
                })}
                :
              </Text>
              <NumericTextField
                width="100%"
                value={formData.quantity}
                onChange={(value) => handleFieldChange("quantity", value)}
              />
            </Flex>
          </Flex>
          <Flex direction="row" gap="size-200" alignItems="end">
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("vat", { ns: "layout_components", defaultValue: "VAT" })}:
              </Text>
              <Picker
                width="100%"
                UNSAFE_className={`${styles.picker_field} borderGrey`}
                selectedKey={formData.vatValue}
                onSelectionChange={(key) => handleFieldChange("vatValue", key)}
              >
                <Item key="0">DPH 0%</Item>
                <Item key="10">DPH 10%</Item>
                <Item key="15">DPH 15%</Item>
                <Item key="21">DPH 21%</Item>
              </Picker>
            </Flex>
            <Flex direction="column" flex={1} gap="size-50">
              <Text UNSAFE_className={styles.field_label}>
                {t("total_price", {
                  ns: "layout_components",
                  defaultValue: "Total Price",
                })}
                :
              </Text>
              <NumericTextField
                width="100%"
                value={formData.totalAmount}
                isReadOnly
                prefix={formData.currency || "CZK"}
              />
            </Flex>
          </Flex>
        </View>

        {/* ── Section 6: Attributes ───────────────────────────── */}
        {(formData.attrib0Name ||
          formData.attrib1Name ||
          formData.attrib2Name ||
          formData.attrib3Name) && (
          <View UNSAFE_className={styles.section_card}>
            {(formData.attrib0Name || formData.attrib1Name) && (
              <Flex direction="row" gap="size-200" alignItems="end">
                {formData.attrib0Name && (
                  <Flex direction="column" flex={1} gap="size-50">
                    <Text UNSAFE_className={styles.field_label}>
                      {formData.attrib0Name}
                    </Text>
                    <TextField
                      width="100%"
                      value={formData.attrib0}
                      onChange={(value) => handleFieldChange("attrib0", value)}
                    />
                  </Flex>
                )}
                {formData.attrib1Name && (
                  <Flex direction="column" flex={1} gap="size-50">
                    <Text UNSAFE_className={styles.field_label}>
                      {formData.attrib1Name}
                    </Text>
                    <Picker
                      width="100%"
                      UNSAFE_className={`${styles.picker_field} datePicker`}
                      selectedKey={formData.attrib1_id}
                      onSelectionChange={(key) =>
                        handleFieldChange("attrib1_id", key)
                      }
                    >
                      {activeAttributesData[1]?.codebooks &&
                        activeAttributesData[1].codebooks.map((cb: any) => (
                          <Item key={cb.id}>{cb.name}</Item>
                        ))}
                    </Picker>
                  </Flex>
                )}
              </Flex>
            )}
            {(formData.attrib2Name || formData.attrib3Name) && (
              <Flex direction="row" gap="size-200" alignItems="end">
                {formData.attrib2Name && (
                  <Flex direction="column" flex={1} gap="size-50">
                    <Text UNSAFE_className={styles.field_label}>
                      {formData.attrib2Name}
                    </Text>
                    <TextField
                      width="100%"
                      value={formData.attrib2}
                      onChange={(value) => handleFieldChange("attrib2", value)}
                    />
                  </Flex>
                )}
                {formData.attrib3Name && (
                  <Flex direction="column" flex={1} gap="size-50">
                    <Text UNSAFE_className={styles.field_label}>
                      {formData.attrib3Name}
                    </Text>
                    <TextField
                      width="100%"
                      value={formData.attrib3}
                      onChange={(value) => handleFieldChange("attrib3", value)}
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </View>
        )}

        {/* ── Section 7: Description ──────────────────────────── */}
        <View UNSAFE_className={styles.section_card}>
          <Flex direction="column" gap="size-50">
            <Text UNSAFE_className={styles.field_label}>
              {t("description", {
                ns: "layout_components",
                defaultValue: "Description",
              })}
              :
            </Text>
            <TextArea
              width="100%"
              height="size-800"
              value={formData.note}
              onChange={(value) => handleFieldChange("note", value)}
            />
          </Flex>
        </View>
      </Flex>
    );
  }

  function dialogFooter(): React.ReactElement {
    const ipadProBtnHeight = isIPadPro ? "44px" : undefined;
    return (
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        gap="size-200"
        UNSAFE_style={{ flexWrap: "wrap", rowGap: isIPadPro ? "12px" : "8px" }}
      >
        <Text
          UNSAFE_className={styles.total_text}
          UNSAFE_style={{ fontSize: isIPadPro ? "16px" : undefined }}
        >
          {t("total", { ns: "layout_components", defaultValue: "Total" })}:{" "}
          {formData.totalAmount} {formData.currency}
          {"  ·  "}
          {t("including_vat", {
            ns: "layout_components",
            defaultValue: "Including VAT",
          })}
          : {calculateIncludingVAT()} {formData.currency}
        </Text>
        <Flex
          direction="row"
          gap="size-150"
          alignItems="center"
          UNSAFE_style={{ flexShrink: 0 }}
        >
          <Button
            variant="secondary"
            onPress={handleClose}
            UNSAFE_className={styles.cancel_button}
            UNSAFE_style={{ height: ipadProBtnHeight }}
          >
            {t("cancel", { ns: "layout_components", defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="secondary"
            onPress={handleSaveAndNew}
            isDisabled={showLoader}
            UNSAFE_className={styles.save_and_new_button}
            UNSAFE_style={{ height: ipadProBtnHeight }}
          >
            {t("save_and_new", {
              ns: "layout_components",
              defaultValue: "Save and New",
            })}
          </Button>
          <Button
            variant="cta"
            onPress={() => handleSave()}
            isDisabled={showLoader}
            UNSAFE_className={styles.save_button}
            UNSAFE_style={{ height: ipadProBtnHeight }}
          >
            {showLoader ? (
              <ProgressCircle
                aria-label={t("saving", {
                  ns: "layout_components",
                  defaultValue: "Saving…",
                })}
                isIndeterminate
                size="S"
              />
            ) : (
              t("save", { ns: "layout_components", defaultValue: "Save" })
            )}
          </Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <DialogContainer onDismiss={handleClose}>
      {visibility && (
        <Dialog
          id="item-create-update-dialog"
          UNSAFE_style={{
            width: isMobile ? "100%" : "100%",
            height: isMobile ? "100vh" : "90vh",
            maxWidth: isMobile ? "100%" : "100%",
            maxHeight: "90vh",
          }}
        >
          <Heading>
            <Text UNSAFE_className={styles.header_title}>
              {t("sales_item", {
                ns: "layout_components",
                defaultValue: "Sales Item",
              })}
            </Text>
          </Heading>
          <Divider />
          <Content UNSAFE_style={{ padding: 0, overflow: "hidden" }}>
            <Flex
              direction="column"
              width="100%"
              height="100%"
              UNSAFE_style={{ minHeight: 0 }}
            >
              <View
                UNSAFE_style={{
                  overflowY: "auto",
                  flex: 1,
                }}
              >
                {dialogContent()}
              </View>
              <Divider />
              <View
                UNSAFE_style={{
                  paddingTop: isIPadPro ? "14px" : "10px",
                  paddingBottom: isIPadPro ? "6px" : undefined,
                }}
              >
                {dialogFooter()}
              </View>
            </Flex>
          </Content>
        </Dialog>
      )}
    </DialogContainer>
  );
};

export default ItemCreateUpdate;
